import type { AxiosPromise, AxiosResponse } from "axios";
import { route } from "../../shim/jsbridge";

import { AxiosApiWrapper } from "../../services/api/AxiosApiWrapper";
import {
  AnalysisStatusResponse,
  GeneralInfoResponse as CapeGeneralInfoResponse,
  CheckPdfResponse,
  CiscoCounts,
  CiscoDisplayStatus,
  CiscoShowLinks,
  CiscoSmaDroppedFiles,
  CiscoSmaGeneralInfo,
  CiscoSmaIocs,
  CiscoSmaNetworking,
  CiscoSmaSignatures,
  IYaraStringMatchesResponse,
  ReportSummary,
  TiCardResponse
} from "./@types";
import {
  FileTreeType,
  HeuristicTypesApiResponse,
  PluginResultTypesApiResponse,
  TagTypesApiResponse
} from "./@types/rl_auxiliary_analysis";
import { DroppedFilesResourcesStatus } from "./@types/rldynamicanalysis";
import {
  ILocalSampleSummary,
  ISampleSummaryUrl
} from "./@types/sample_details";
import {
  IHexPreviewResponse,
  ITextPreviewResponse
} from "./@types/visualization";
import {
  CreateLayoutPayload,
  LayoutDetailsResponse,
  LayoutsResponse,
  SaveLayoutPayload
} from "./components/layout-editor/@types";
import { SourcesCountResponse } from "./components/report_summary/@types";
import { getRouteForSampleSummaryRedirect } from "./util";
import { UseGetInteractiveSessionParamsForAnalysisParams } from "./components/interactiveanalysis/hooks/useGetInteractiveSessionParamsForAnalysis";

class Api extends AxiosApiWrapper {
  /**
   * Returns RL Auxiliary Analysis Summary Info
   */
  rlAuxiliaryAnalysisSummaryInfo = (hashValue: string) =>
    this.get(route("api_rl_auxiliary_analysis_summary_info", [hashValue]));

  /**
   * Returns RL Auxiliary Analysis File Tree
   */
  rlAuxiliaryAnalysisFileTree = (
    hashValue: string
  ): AxiosPromise<FileTreeType> =>
    this.get(route("api_rl_auxiliary_analysis_file_tree", [hashValue]));

  /**
   * Returns RL Auxiliary Analysis Heuristics
   */
  rlAuxiliaryAnalysisHeuristics = (
    hashValue: string
  ): AxiosPromise<HeuristicTypesApiResponse> =>
    this.get(route("api_rl_auxiliary_analysis_heuristics", [hashValue]));

  /**
   * Returns RL Auxiliary Analysis Plugin Results
   */
  rlAuxiliaryAnalysisPluginResults = (
    hashValue: string
  ): AxiosPromise<PluginResultTypesApiResponse> =>
    this.get(route("api_rl_auxiliary_analysis_plugin_results", [hashValue]));

  /**
   * Returns RL Auxiliary Analysis Tags Info
   */
  rlAuxiliaryAnalysisTags = (
    hashValue: string
  ): AxiosPromise<TagTypesApiResponse> =>
    this.get(route("api_rl_auxiliary_analysis_tags", [hashValue]));

  /**
   * Returns the display status of RL Auxiliary Analysis
   */
  rlAuxiliaryAnalysisDisplayStatus = (hashValue: string) =>
    this.get(route("api_rl_auxiliary_analysis_display_status", [hashValue]));

  /**
   * Returns whether we display task links for RL Auxiliary Analysis Analysis
   */
  rlAuxiliaryAnalysisShowLinks = () =>
    this.get(route("api_rl_auxiliary_analysis_show_links"));

  /**
   * Return Cisco Secure Malware Analytics (Threat Grid) Analysis Summary Info
   */
  ciscoSecureMalwareAnalyticsSummaryInfo = (
    hash_value: string
  ): AxiosPromise<CiscoSmaGeneralInfo> =>
    this.get(
      route("api_cisco_secure_malware_analytics_summary_info", [hash_value])
    );

  /**
   * Return Cisco Secure Malware Analytics (Threat Grid) Dropped Files
   */
  ciscoSecureMalwareAnalyticsDroppedFiles = (
    hash_value: string
  ): AxiosPromise<CiscoSmaDroppedFiles> =>
    this.get(
      route("api_cisco_secure_malware_analytics_dropped_files", [hash_value])
    );

  /**
   * Return Cisco Secure Malware Analytics (Threat Grid) IOCs
   */
  ciscoSecureMalwareAnalyticsIocs = (
    hash_value: string
  ): AxiosPromise<CiscoSmaIocs> =>
    this.get(route("api_cisco_secure_malware_analytics_iocs", [hash_value]));

  /**
   * Return Cisco Secure Malware Analytics (Threat Grid) Networking Data
   */
  ciscoSecureMalwareAnalyticsNetworking = (
    hash_value: string
  ): AxiosPromise<CiscoSmaNetworking> =>
    this.get(
      route("api_cisco_secure_malware_analytics_networking", [hash_value])
    );

  /**
   * Return Cisco Secure Malware Analytics (Threat Grid) Signatures Data
   */
  ciscoSecureMalwareAnalyticsSignatures = (
    hash_value: string
  ): AxiosPromise<CiscoSmaSignatures> =>
    this.get(
      route("api_cisco_secure_malware_analytics_signatures", [hash_value])
    );

  /**
   * Returns the display status of Cisco Secure Malware Analytics (Threat Grid) Analysis
   */
  ciscoSecureMalwareAnalyticsDisplayStatus = (
    hash_value: string
  ): AxiosPromise<CiscoDisplayStatus> =>
    this.get(
      route("api_cisco_secure_malware_analytics_display_status", [hash_value])
    );

  /**
   * Returns the data counts for Cisco Secure Malware Analytics sections
   */
  ciscoSecureMalwareAnalyticsCounts = (
    hash_value: string
  ): AxiosPromise<CiscoCounts> =>
    this.get(route("api_cisco_secure_malware_analytics_counts", [hash_value]));

  /**
   * Returns whether we display task links for Cisco Secure Malware Analytics (Threat Grid) Analysis
   */
  ciscoSecureMalwareAnalyticsShowLinks = (): AxiosPromise<CiscoShowLinks> =>
    this.get(route("api_cisco_secure_malware_analytics_show_links"));

  getRLSandboxResourcesStatus = (
    hashValue: string,
    analysisId: string,
    isNetwork: boolean
  ): Promise<AxiosResponse<DroppedFilesResourcesStatus>> =>
    this.get(
      route(
        "api_rl_dynamic_analysis_resource_check",
        [hashValue, analysisId],
        isNetwork && { isNetwork }
      )
    );

  downloadRLSandboxExtractedScreenshots = (
    hashValue: string,
    analysisId: string,
    isNetwork: boolean
  ) =>
    this.get(
      route(
        "api_rl_dynamic_analysis_resource_preview_screenshots",
        [hashValue, analysisId],
        isNetwork && { isNetwork }
      )
    );

  downloadRLSandboxScreenshots = (
    hashValue: string,
    analysisId: string,
    isNetwork: boolean
  ) =>
    this.head(
      route(
        "api_rl_dynamic_analysis_resource_screenshots",
        [hashValue, analysisId],
        isNetwork && { isNetwork }
      ),
      {}
    );

  downloadRlSandboxReportPcaps = (
    hashValue: string,
    analysisId: string,
    isNetwork: boolean
  ) =>
    this.head(
      route(
        "api_rl_dynamic_analysis_resource_pcaps",
        [hashValue, analysisId],
        isNetwork && { isNetwork }
      ),
      {}
    );

  downloadRlSandboxReportDroppedFiles = (
    hashValue: string,
    analysisId: string,
    isNetwork: boolean
  ) =>
    this.head(
      route(
        "api_rl_dynamic_analysis_resource_dropped_files",
        [hashValue, analysisId],
        isNetwork && { isNetwork }
      ),
      {}
    );

  downloadRlSandboxReportMemstrings = (
    hashValue: string,
    analysisId: string,
    isNetwork: boolean
  ) =>
    this.head(
      route(
        "api_rl_dynamic_analysis_resource_memstrings",
        [hashValue, analysisId],
        isNetwork && { isNetwork }
      ),
      {}
    );

  sendDroppedFilesToStaticAnalysis = (hashValue: string, analysisId: string) =>
    this.post(
      route("api_rl_dynamic_analysis_dropped_files_analysis", [
        hashValue,
        analysisId
      ])
    );

  getDroppedFilesStaticAnalysisStatus = (
    hashValue: string,
    analysisId: string
  ) =>
    this.get(
      route("api_rl_dynamic_analysis_dropped_files_analysis_status", [
        hashValue,
        analysisId
      ])
    );

  getCapeGeneralInfo = (hash: string): AxiosPromise<CapeGeneralInfoResponse> =>
    this.get(route("api_cape_general_info", [hash]));

  getDynamicAnalysisData = <T>(endpoint: string): AxiosPromise<T> =>
    this.get(endpoint);

  getLocalSampleDetails = (
    hash?: string,
    sourceId?: number | string,
    relationshipId?: number | string,
    droppedSourceId?: number | string,
    parentHash?: string
  ): AxiosPromise<ILocalSampleSummary> =>
    this.get(route("api_sample_summary_details_local", [hash, sourceId]), {
      params: {
        r: relationshipId,
        dropped_source_id: droppedSourceId,
        p: parentHash
      }
    });

  getTiCardData = (threatName: string): AxiosPromise<TiCardResponse> =>
    this.get(route("ti-card-get", [threatName]));

  getSampleSummaryUrl = (
    hash_value: string,
    droppedSourceId?: number | string | null,
    uploadId?: number | string | null,
    section?: string
  ): AxiosPromise<ISampleSummaryUrl> =>
    this.get(
      route(getRouteForSampleSummaryRedirect(section), {
        hash_value,
        section
      }),
      {
        params: { dropped_source_id: droppedSourceId, upload_id: uploadId }
      }
    );

  getHexPreviewData = (
    sha1: string,
    searchAddress?: string,
    startAddress = 0
  ): AxiosPromise<IHexPreviewResponse> => {
    const data =
      searchAddress !== undefined
        ? { search_address: searchAddress }
        : { start_address: startAddress || 0 };
    return this.get(route("api_hex_preview", [sha1], data));
  };

  getTextPreviewData = (
    sha1: string,
    startAddress = 0,
    beautify?: boolean
  ): AxiosPromise<ITextPreviewResponse> => {
    const data = { start_address: startAddress };
    return this.get(
      route(
        beautify ? "api_beautify_text_preview" : "api_text_preview",
        [sha1],
        data
      )
    );
  };

  getImagePreviewThumbnail = async (
    sha1: string,
    size: "small" | "normal" | "large" = "large"
  ): Promise<string> => {
    const imageUrl = route("api_thumbnail", [sha1, size]);
    await this.head(imageUrl);
    return imageUrl;
  };

  getYaraStringMatches = (
    hash: string,
    yaraTestRunResultId?: number
  ): AxiosPromise<IYaraStringMatchesResponse> =>
    this.get(
      route(
        "api-yara-string-matches",
        { hash_value: hash },
        yaraTestRunResultId
          ? { yara_test_run_result_id: yaraTestRunResultId }
          : {}
      )
    );

  getSampleReportSummary = (
    hash: string,
    sourceId: number,
    relationshipId?: number
  ): Promise<AxiosResponse<ReportSummary>> =>
    this.get(
      route(
        "api-summary-report",
        { hash_value: hash, source_id: sourceId },
        relationshipId && { r: relationshipId }
      )
    );

  getSampleReportInterestingStrings = (
    hash: string,
    sourceId: number,
    relationshipId?: number
  ) =>
    this.get(
      route(
        "api-summary-report-interesting-strings",
        { hash_value: hash, source_id: sourceId },
        relationshipId && { r: relationshipId }
      )
    );

  getSampleAnalysisStatus = (
    hash: string
  ): AxiosPromise<AnalysisStatusResponse> =>
    this.get(route("sample_analysis_status", { hash_value: hash }));

  makePdf = (hash_value: string, source_id: number, relationshipId?: number) =>
    this.get(
      route(
        "pdf_summary_make",
        { hash_value, source_id },
        relationshipId && { r: relationshipId }
      )
    );

  downloadPdf = (
    hash_value: string,
    source_id: number,
    relationshipId?: number
  ) =>
    this.get(
      route(
        "pdf_summary_get",
        { hash_value, source_id },
        relationshipId && { r: relationshipId }
      )
    );

  checkPdf = (
    hash_value: string,
    source_id: number,
    relationshipId?: number
  ): AxiosPromise<CheckPdfResponse> =>
    this.get(
      route(
        "pdf_summary_check",
        { hash_value, source_id },
        relationshipId && { r: relationshipId }
      )
    );

  createLayout = ({
    type,
    name,
    content
  }: CreateLayoutPayload): AxiosPromise<{ id: number }> =>
    this.post(route("api-samples-layout-type-view", [type]), { name, content });

  getLayouts = (): AxiosPromise<LayoutsResponse> =>
    this.get(route("api-samples-layouts"));

  getLayoutDetails = (
    id: number | string,
    type: string
  ): AxiosPromise<LayoutDetailsResponse> =>
    this.get(route("api-samples-layout-details-view", [type, id]));

  deleteLayout = (id: string, type: string) =>
    this.delete(route("api-samples-layout-details-view", [type, id]));

  updateLayout = ({ id, type, content }: SaveLayoutPayload) =>
    this.put(route("api-samples-layout-details-view", [type, id]), { content });

  getSourcesCount = (sha1: string): AxiosPromise<SourcesCountResponse> =>
    this.get(route("sources_counts", [sha1]));

  getSampleSpectraCoreInfo = (hash: string) =>
    this.get(route("api-sample-ticore-v2", [hash], { fields: "info" }));

  getInteractiveSessionParamsForHash = (hash?: string) =>
    this.get(route("interactive_analysis_parameters_for_hash", [hash]));

  getInteractiveSessionParamsForUploadId = (uploadId?: string) =>
    this.get(
      route("interactive_analysis_parameters_for_upload_id", [uploadId])
    );

  getInteractiveSessionParamsForAnalysis = (
    params?: UseGetInteractiveSessionParamsForAnalysisParams
  ) =>
    this.get(
      route("interactive_analysis_parameters_for_analysis", [
        params?.hash,
        params?.analysisId
      ])
    );

  terminateInteractiveAnalysis = (hash?: string) =>
    this.post(route("rl_interactive_analysis_terminate", [hash]));
}

export default new Api();
